import React, { useEffect, useState } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getReviewCampaigns,
  createReviewCampaign,
  activateReviewCampaign,
  completeReviewCampaign,
  getMyReviewItems,
  decideReviewItem
} from '../services/api';
import './AccessReviews.css';

const AccessReviews = () => {
  const { user: currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [myItems, setMyItems] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', endDate: '' });
  const [message, setMessage] = useState('');

  const isAdmin = currentUser?.role === 'admin';

  const fetchData = async () => {
    try {
      setLoadingData(true);
      const [campaignsRes, itemsRes] = await Promise.all([
        isAdmin ? getReviewCampaigns().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        getMyReviewItems().catch(() => ({ data: [] }))
      ]);
      setCampaigns(campaignsRes.data || []);
      setMyItems(itemsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/login');
      return;
    }
    if (currentUser) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isAdmin, loading, navigate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createReviewCampaign(form);
      setShowCreate(false);
      setForm({ title: '', description: '', endDate: '' });
      setMessage('Campaign created');
      fetchData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to create campaign');
    }
  };

  const handleActivate = async (id) => {
    try {
      await activateReviewCampaign(id);
      setMessage('Campaign activated. Reviewers were notified.');
      fetchData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to activate campaign');
    }
  };

  const handleComplete = async (id) => {
    if (!window.confirm('Complete campaign and revoke all flagged access?')) return;
    try {
      const res = await completeReviewCampaign(id);
      setMessage(`Campaign completed. ${res.data.revoked} access(es) revoked.`);
      fetchData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to complete campaign');
    }
  };

  const handleDecide = async (campaignId, itemIndex, decision) => {
    try {
      await decideReviewItem(campaignId, itemIndex, decision);
      fetchData();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to save decision');
    }
  };

  const pendingItems = myItems.filter((i) => i.item.decision === 'pending');
  const itemPages = usePagination(pendingItems, 10, pendingItems.length);
  const campaignPages = usePagination(campaigns, 8, campaigns.length);

  if (loading || loadingData) return <div className="access-reviews"><div className="spinner" /></div>;

  return (
    <div className="access-reviews">
      <div className="access-reviews-header">
        <div>
          <h1>Access Review Campaigns</h1>
          <p>Periodic certification of repository access for compliance</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>New campaign</button>
        )}
      </div>

      {message && <div className="alert alert-info">{message}</div>}

      {pendingItems.length > 0 && (
        <section className="card review-section">
          <h2>My review items</h2>
          <div className="review-items">
            {itemPages.pageItems.map((entry) => (
              <div key={`${entry.campaignId}-${entry.itemIndex}`} className="review-item">
                <div>
                  <strong>{entry.campaignTitle}</strong>
                  <p>{entry.item.repository?.fullName} — {entry.item.githubUsername} ({entry.item.permission})</p>
                </div>
                <div className="review-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => handleDecide(entry.campaignId, entry.itemIndex, 'certify')}>Certify</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDecide(entry.campaignId, entry.itemIndex, 'revoke')}>Revoke</button>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            page={itemPages.page}
            totalPages={itemPages.totalPages}
            total={itemPages.total}
            pageSize={itemPages.pageSize}
            onPageChange={itemPages.setPage}
            label="items"
          />
        </section>
      )}

      {isAdmin && (
        <section className="card review-section">
          <h2>Campaigns</h2>
          {campaigns.length === 0 ? (
            <p className="empty-text">No campaigns yet</p>
          ) : (
            <div className="campaign-list">
              {campaignPages.pageItems.map((c) => {
                const pending = c.items?.filter((i) => i.decision === 'pending').length || 0;
                const revoke = c.items?.filter((i) => i.decision === 'revoke').length || 0;
                return (
                  <div key={c._id} className="campaign-card">
                    <div className="campaign-header">
                      <strong>{c.title}</strong>
                      <span className={`status-badge status-${c.status}`}>{c.status}</span>
                    </div>
                    <p>{c.description}</p>
                    <div className="campaign-stats">
                      <span>{c.items?.length || 0} items</span>
                      <span>{pending} pending</span>
                      <span>{revoke} to revoke</span>
                    </div>
                    <div className="campaign-actions">
                      {c.status === 'draft' && (
                        <button className="btn btn-primary btn-sm" onClick={() => handleActivate(c._id)}>Activate</button>
                      )}
                      {c.status === 'active' && (
                        <button className="btn btn-secondary btn-sm" onClick={() => handleComplete(c._id)}>Complete & apply revocations</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <Pagination
            page={campaignPages.page}
            totalPages={campaignPages.totalPages}
            total={campaignPages.total}
            pageSize={campaignPages.pageSize}
            onPageChange={campaignPages.setPage}
            label="campaigns"
          />
        </section>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create access review campaign</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">End date (optional)</label>
                <input type="date" className="form-input" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Create</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessReviews;
